import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

const LightboxContext = createContext({ open: () => {} });
export const useLightbox = () => useContext(LightboxContext);

const MIN_SCALE = 1;
const MAX_SCALE = 5;
const clamp = (v, mn, mx) => Math.max(mn, Math.min(mx, v));

function LightboxModal({ src, alt, onClose }) {
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [entered, setEntered] = useState(false);

  const dragRef = useRef(null);
  const pinchRef = useRef(null);
  const gestureActive = useRef(false);
  const [, force] = useState(0);

  // mount animation
  useEffect(() => {
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, []);

  // body scroll lock + ESC + cleanup
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const reset = () => {
    setScale(1);
    setTx(0);
    setTy(0);
  };

  const onWheel = useCallback((e) => {
    e.preventDefault();
    const delta = -e.deltaY * 0.0025;
    setScale((s) => clamp(s + s * delta * 1.5, MIN_SCALE, MAX_SCALE));
  }, []);

  const onDoubleClick = useCallback(() => {
    setScale((s) => {
      if (s > 1.1) {
        setTx(0);
        setTy(0);
        return 1;
      }
      return 2.5;
    });
  }, []);

  // touch handlers — pinch + pan
  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      const [a, b] = [e.touches[0], e.touches[1]];
      const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      pinchRef.current = { startDist: dist, baseScale: scale };
      dragRef.current = null;
      gestureActive.current = true;
      force((x) => x + 1);
    } else if (e.touches.length === 1 && scale > 1) {
      const t = e.touches[0];
      dragRef.current = {
        startX: t.clientX,
        startY: t.clientY,
        baseTx: tx,
        baseTy: ty,
      };
      pinchRef.current = null;
      gestureActive.current = true;
      force((x) => x + 1);
    }
  };
  const onTouchMove = (e) => {
    if (e.touches.length === 2 && pinchRef.current) {
      e.preventDefault();
      const [a, b] = [e.touches[0], e.touches[1]];
      const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      const ratio = dist / pinchRef.current.startDist;
      setScale(
        clamp(pinchRef.current.baseScale * ratio, MIN_SCALE, MAX_SCALE)
      );
    } else if (e.touches.length === 1 && dragRef.current) {
      e.preventDefault();
      const t = e.touches[0];
      setTx(dragRef.current.baseTx + (t.clientX - dragRef.current.startX));
      setTy(dragRef.current.baseTy + (t.clientY - dragRef.current.startY));
    }
  };
  const onTouchEnd = () => {
    dragRef.current = null;
    pinchRef.current = null;
    gestureActive.current = false;
    force((x) => x + 1);
  };

  // mouse drag when zoomed
  const onMouseDown = (e) => {
    if (scale <= 1) return;
    e.preventDefault();
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      baseTx: tx,
      baseTy: ty,
    };
    gestureActive.current = true;
    force((x) => x + 1);
  };
  const onMouseMove = (e) => {
    if (!dragRef.current) return;
    setTx(dragRef.current.baseTx + (e.clientX - dragRef.current.startX));
    setTy(dragRef.current.baseTy + (e.clientY - dragRef.current.startY));
  };
  const onMouseUp = () => {
    dragRef.current = null;
    gestureActive.current = false;
    force((x) => x + 1);
  };

  return createPortal(
    <div
      data-testid="logo-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={alt}
      className={`fixed inset-0 z-[100] flex items-center justify-center transition-opacity duration-300 ${
        entered ? "opacity-100" : "opacity-0"
      }`}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp}
      onMouseMove={onMouseMove}
    >
      {/* Backdrop — click to close */}
      <button
        type="button"
        data-testid="lightbox-backdrop"
        onClick={onClose}
        aria-label="Close fullscreen view"
        className="absolute inset-0 bg-black/95 backdrop-blur-sm cursor-zoom-out"
      />

      {/* Close X */}
      <button
        type="button"
        onClick={onClose}
        data-testid="lightbox-close"
        aria-label="Close"
        className="absolute top-5 right-5 z-10 inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/15 bg-white/5 backdrop-blur-md text-white/90 transition-colors duration-300 hover:bg-white/10 hover:border-white/30"
      >
        <X size={20} />
      </button>

      {/* Reset zoom (desktop) */}
      {scale > 1.05 && (
        <button
          type="button"
          onClick={reset}
          data-testid="lightbox-reset"
          aria-label="Reset zoom"
          className="absolute top-5 right-20 z-10 hidden md:inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 backdrop-blur-md px-4 py-2 text-[11px] uppercase font-mono tracking-[0.22em] text-white/80 hover:bg-white/10"
        >
          Reset
        </button>
      )}

      {/* Image */}
      <div
        onWheel={onWheel}
        onDoubleClick={onDoubleClick}
        onMouseDown={onMouseDown}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        style={{
          touchAction: "none",
          cursor:
            scale > 1
              ? gestureActive.current
                ? "grabbing"
                : "grab"
              : "zoom-in",
          transform: `translate3d(${tx}px, ${ty}px, 0) scale(${
            entered ? scale : 0.92
          })`,
          transition: gestureActive.current
            ? "none"
            : "transform 0.35s cubic-bezier(0.22, 1, 0.36, 1)",
        }}
        className="relative max-w-[92vw] max-h-[88vh] select-none"
      >
        {!loaded && (
          <div
            data-testid="lightbox-loading"
            className="absolute inset-0 flex items-center justify-center text-white/60 text-[11px] font-mono uppercase tracking-[0.22em]"
          >
            <span className="inline-block h-2 w-2 rounded-full bg-[#2997FF] animate-pulse mr-3" />
            Loading hi-res
          </div>
        )}
        <img
          src={src}
          alt={alt}
          onLoad={() => setLoaded(true)}
          draggable={false}
          className={`block max-w-[92vw] max-h-[88vh] w-auto h-auto object-contain rounded-2xl shadow-[0_40px_120px_-20px_rgba(0,0,0,0.8)] transition-opacity duration-500 ${
            loaded ? "opacity-100" : "opacity-0"
          }`}
        />
      </div>

      {/* Hint */}
      <div className="pointer-events-none absolute bottom-6 left-1/2 -translate-x-1/2 hidden md:inline-flex items-center gap-3 px-4 py-2 rounded-full border border-white/10 bg-white/5 backdrop-blur-md text-[11px] uppercase font-mono tracking-[0.22em] text-white/60">
        Scroll · Double-click · Esc
      </div>
      <div className="pointer-events-none absolute bottom-6 left-1/2 -translate-x-1/2 inline-flex md:hidden items-center gap-3 px-4 py-2 rounded-full border border-white/10 bg-white/5 backdrop-blur-md text-[10px] uppercase font-mono tracking-[0.22em] text-white/60">
        Pinch to zoom · Tap outside
      </div>
    </div>,
    document.body
  );
}

export function LightboxProvider({ children }) {
  const [state, setState] = useState({ src: null, alt: "" });
  const open = useCallback((src, alt = "") => setState({ src, alt }), []);
  const close = useCallback(() => setState({ src: null, alt: "" }), []);
  return (
    <LightboxContext.Provider value={{ open }}>
      {children}
      {state.src && (
        <LightboxModal src={state.src} alt={state.alt} onClose={close} />
      )}
    </LightboxContext.Provider>
  );
}
